import { FC, useEffect, useMemo, useState } from 'react'
import * as XLSX from 'sheetjs-style'
import jsPDF from 'jspdf'
import Select from 'react-select'
import { useSessionHistory } from '../hooks/useSessionHistory'
import { SellerServiceSessionItem } from '../types'

interface HistoryTabProps {
  pluginspaceId: number
  currentUserId: number
  isPrivileged: boolean
}

const getFileTimestamp = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  const seconds = String(now.getSeconds()).padStart(2, '0')
  return `${year}${month}${day}-${hours}${minutes}${seconds}`
}

export const HistoryTab: FC<HistoryTabProps> = ({ pluginspaceId, currentUserId, isPrivileged }) => {
  const { sessions, loading, loadHistory } = useSessionHistory(pluginspaceId)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [sellerFilter, setSellerFilter] = useState('all')
  const [sellerSearch, setSellerSearch] = useState('')
  const [outcomeFilter, setOutcomeFilter] = useState('all')
  const [outcomeSearch, setOutcomeSearch] = useState('')
  const [selectedMotive, setSelectedMotive] = useState<string>('all')
  const [motiveSearch, setMotiveSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const pageSizeOptions = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100]

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0]
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    setStartDate(yesterday)
    setEndDate(today)
    loadHistory(yesterday, today)
  }, [loadHistory])

  const handleFilterApply = async () => {
    if (startDate && endDate) {
      setCurrentPage(1)
      await loadHistory(startDate, endDate)
    }
  }

  const handleClearFilters = async () => {
    const today = new Date().toISOString().split('T')[0]

    setStartDate(today)
    setEndDate(today)
    setSellerFilter('all')
    setSellerSearch('')
    setOutcomeFilter('all')
    setOutcomeSearch('')
    setSelectedMotive('all')
    setMotiveSearch('')
    setCurrentPage(1)

    await loadHistory(today, today)
  }

  const parseLocalStartDate = (dateValue: string) => new Date(`${dateValue}T00:00:00`)
  const parseLocalEndDate = (dateValue: string) => new Date(`${dateValue}T23:59:59.999`)

  const formatDateTime = (value?: string | null) => {
    if (!value) return '-'
    return new Date(value).toLocaleString('pt-BR')
  }

  const getMotiveLabel = (session: SellerServiceSessionItem) => {
    const reasons = session?.noSaleReasons || []
    if (!Array.isArray(reasons) || reasons.length === 0) return '-'

    const firstReason = reasons[0]
    const label =
      firstReason?.reason?.saleReasonCode ||
      firstReason?.note ||
      firstReason?.productName ||
      ''

    if (!label) return '-'
    return String(label)
  }

  const getOutcomeColor = (outcome: string | null) => {
    switch (outcome) {
      case 'sale_completed':
        return 'success'
      case 'pending_checkout':
        return 'warning'
      case 'no_sale':
        return 'danger'
      case 'abandoned':
        return 'secondary'
      default:
        return 'light'
    }
  }

  const getOutcomeLabel = (outcome: string | null) => {
    switch (outcome) {
      case 'sale_completed':
        return 'Venda Concluída'
      case 'pending_checkout':
        return 'Esperando Checkout'
      case 'no_sale':
        return 'Sem Venda'
      case 'abandoned':
        return 'Abandonado'
      default:
        return '-'
    }
  }

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '-'
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes}m ${secs}s`
  }

  const baseSessions = isPrivileged
    ? sessions
    : sessions.filter((session) => session.sellerId === currentUserId)

  const sellerOptions = useMemo(() => {
    const unique = new Map<number, string>()
    baseSessions.forEach((session) => {
      unique.set(session.sellerId, session.sellerName || `Vendedor ${session.sellerId}`)
    })

    const options = Array.from(unique.entries())
      .map(([id, name]) => ({ value: String(id), label: name }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'))

    return [{ value: 'all', label: 'Todos os vendedores' }, ...options]
  }, [baseSessions])

  const outcomeOptions = useMemo(
    () => [
      { value: 'all', label: 'Todos os resultados' },
      { value: 'sale_completed', label: 'Venda Concluída' },
      { value: 'pending_checkout', label: 'Esperando Checkout' },
      { value: 'no_sale', label: 'Sem Venda' },
      { value: 'abandoned', label: 'Abandonado' },
    ],
    []
  )

  const motiveOptions = useMemo(() => {
    const unique = new Set<string>()
    baseSessions.forEach((session) => {
      const label = getMotiveLabel(session)
      if (label && label !== '-') {
        unique.add(label)
      }
    })

    const options = Array.from(unique)
      .sort((a, b) => a.localeCompare(b, 'pt-BR'))
      .map((value) => ({ value, label: value }))

    return [{ value: 'all', label: 'Todos os motivos' }, ...options]
  }, [baseSessions])

  const filteredSessions = useMemo(() => {
    let sessionsFiltered = baseSessions

    if (startDate && endDate) {
      const startBoundary = parseLocalStartDate(startDate)
      const endBoundary = parseLocalEndDate(endDate)

      sessionsFiltered = sessionsFiltered.filter((session) => {
        const sessionDate = new Date(session.startedAt)
        return sessionDate >= startBoundary && sessionDate <= endBoundary
      })
    }

    if (sellerFilter !== 'all') {
      sessionsFiltered = sessionsFiltered.filter((session) => String(session.sellerId) === sellerFilter)
    }

    if (sellerSearch.trim()) {
      const search = sellerSearch.trim().toLowerCase()
      sessionsFiltered = sessionsFiltered.filter((session) => {
        const sellerName = (session.sellerName || `Vendedor ${session.sellerId}`).toLowerCase()
        return sellerName.includes(search) || String(session.sellerId).includes(search)
      })
    }

    if (outcomeFilter !== 'all') {
      sessionsFiltered = sessionsFiltered.filter((session) => session.outcome === outcomeFilter)
    }

    if (outcomeSearch.trim()) {
      const search = outcomeSearch.trim().toLowerCase()
      sessionsFiltered = sessionsFiltered.filter((session) => {
        const outcomeLabel = getOutcomeLabel(session.outcome).toLowerCase()
        const outcomeValue = (session.outcome || '').toLowerCase()
        return outcomeLabel.includes(search) || outcomeValue.includes(search)
      })
    }

    if (selectedMotive !== 'all') {
      sessionsFiltered = sessionsFiltered.filter((session) => getMotiveLabel(session) === selectedMotive)
    }

    if (motiveSearch.trim()) {
      const search = motiveSearch.trim().toLowerCase()
      sessionsFiltered = sessionsFiltered.filter((session) => getMotiveLabel(session).toLowerCase().includes(search))
    }

    return sessionsFiltered
  }, [
    baseSessions,
    startDate,
    endDate,
    sellerFilter,
    sellerSearch,
    outcomeFilter,
    outcomeSearch,
    selectedMotive,
    motiveSearch,
  ])

  const localSummary = useMemo(() => {
    const totalSessions = filteredSessions.length
    const totalSales = filteredSessions.filter(
      (s) => s.outcome === 'sale_completed' || s.outcome === 'pending_checkout'
    ).length
    const totalNoSales = filteredSessions.filter((s) => s.outcome === 'no_sale').length
    const durations = filteredSessions
      .map((s) => s.durationSeconds || 0)
      .filter((duration) => duration > 0)

    const avgDurationSeconds =
      durations.length > 0
        ? durations.reduce((acc, duration) => acc + duration, 0) / durations.length
        : 0

    return {
      totalSessions,
      totalSales,
      totalNoSales,
      avgDurationSeconds,
    }
  }, [filteredSessions])

  const totalPages = Math.max(1, Math.ceil(filteredSessions.length / pageSize))

  const pagedSessions = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    return filteredSessions.slice(startIndex, startIndex + pageSize)
  }, [filteredSessions, currentPage, pageSize])

  useEffect(() => {
    setCurrentPage(1)
  }, [pageSize, filteredSessions.length])

  const handleExportExcel = () => {
    if (filteredSessions.length === 0) return

    const rows = filteredSessions.map((session) => ({
      Vendedor: session.sellerName || `Vendedor ${session.sellerId}`,
      Posicao: session.queuePositionAtStart,
      Inicio: formatDateTime(session.startedAt),
      Fim: formatDateTime(session.finishedAt),
      Duracao: formatDuration(session.durationSeconds),
      Resultado: getOutcomeLabel(session.outcome),
      Motivo: getMotiveLabel(session),
    }))

    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'HistoricoAtendimento')
    const timestamp = getFileTimestamp()
    XLSX.writeFile(wb, `seller-queue-historico-ps-${pluginspaceId}-${timestamp}.xlsx`)
  }

  const handleExportPdf = () => {
    if (filteredSessions.length === 0) return

    try {
      const doc = new jsPDF({ orientation: 'landscape' })
      const jsPDFAutoTable = require('jspdf-autotable')

      doc.setFontSize(14)
      doc.text('Historico de Atendimentos', 14, 15)
      doc.setFontSize(10)
      doc.text(`Periodo: ${startDate} ate ${endDate}`, 14, 22)

      jsPDFAutoTable.autoTable(doc, {
        startY: 28,
        head: [['Vendedor', 'Posicao', 'Inicio', 'Fim', 'Duracao', 'Resultado', 'Motivo']],
        body: filteredSessions.map((session) => [
          session.sellerName || `Vendedor ${session.sellerId}`,
          String(session.queuePositionAtStart || '-'),
          formatDateTime(session.startedAt),
          formatDateTime(session.finishedAt),
          formatDuration(session.durationSeconds),
          getOutcomeLabel(session.outcome),
          getMotiveLabel(session),
        ]),
        theme: 'grid',
        headStyles: { fillColor: [13, 110, 253], textColor: 255 },
      })

      const timestamp = getFileTimestamp()
      doc.save(`seller-queue-historico-ps-${pluginspaceId}-${timestamp}.pdf`)
    } catch (error) {
      console.error('Erro ao exportar PDF do histórico:', error)
    }
  }

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ minHeight: '300px' }}>
        <div className='spinner-border' role='status'>
          <span className='sr-only'>Carregando...</span>
        </div>
      </div>
    )
  }

  return (
    <div>
      {localSummary && (
        <div className='row g-3 mb-5'>
          <div className='col-md-6 col-lg-3'>
            <div className='card bg-light-info'>
              <div className='card-body'>
                <div className='fs-4 fw-bold text-info'>{localSummary.totalSessions}</div>
                <div className='fs-7 text-gray-700'>Total de Atendimentos</div>
              </div>
            </div>
          </div>
          <div className='col-md-6 col-lg-3'>
            <div className='card bg-light-success'>
              <div className='card-body'>
                <div className='fs-4 fw-bold text-success'>{localSummary.totalSales}</div>
                <div className='fs-7 text-gray-700'>Vendas</div>
              </div>
            </div>
          </div>
          <div className='col-md-6 col-lg-3'>
            <div className='card bg-light-danger'>
              <div className='card-body'>
                <div className='fs-4 fw-bold text-danger'>{localSummary.totalNoSales}</div>
                <div className='fs-7 text-gray-700'>Sem Venda</div>
              </div>
            </div>
          </div>
          <div className='col-md-6 col-lg-3'>
            <div className='card bg-light-warning'>
              <div className='card-body'>
                <div className='fs-4 fw-bold text-warning'>
                  {Math.round(localSummary.avgDurationSeconds / 60)}m
                </div>
                <div className='fs-7 text-gray-700'>Duração Média</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className='card mb-5'>
        <div className='card-header'>
          <h3 className='card-title'>Filtros</h3>
        </div>
        <div className='card-body'>
          <div className='row g-3'>
            <div className='col-md-3'>
              <div className='row g-2'>
                <div className='col-6'>
                  <label className='form-label'>Data Início</label>
                  <input
                    type='date'
                    className='form-control'
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className='col-6'>
                  <label className='form-label'>Data Fim</label>
                  <input
                    type='date'
                    className='form-control'
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className='col-md-3'>
              <label className='form-label d-block opacity-0 user-select-none'>Ações</label>
              <div className='d-flex align-items-center gap-2 flex-nowrap'>
                <button
                  className='btn btn-primary d-flex align-items-center justify-content-center'
                  style={{ width: 38, height: 38, padding: 0 }}
                  onClick={handleFilterApply}
                  disabled={loading}
                  title='Filtrar resultados'
                  aria-label='Filtrar resultados'
                >
                  <i className='fas fa-search'></i>
                </button>
                <button
                  className='btn btn-light d-flex align-items-center justify-content-center'
                  style={{ width: 38, height: 38, padding: 0 }}
                  onClick={handleClearFilters}
                  disabled={loading}
                  title='Limpar filtros'
                  aria-label='Limpar filtros'
                >
                  <i className='fas fa-eraser'></i>
                </button>
                <button
                  className='btn btn-light-success d-flex align-items-center justify-content-center'
                  style={{ width: 38, height: 38, padding: 0 }}
                  onClick={handleExportExcel}
                  disabled={filteredSessions.length === 0}
                  title='Exportar para Excel'
                  aria-label='Exportar para Excel'
                >
                  <i className='fas fa-file-excel'></i>
                </button>
                <button
                  className='btn btn-light-danger d-flex align-items-center justify-content-center'
                  style={{ width: 38, height: 38, padding: 0 }}
                  onClick={handleExportPdf}
                  disabled={filteredSessions.length === 0}
                  title='Exportar para PDF'
                  aria-label='Exportar para PDF'
                >
                  <i className='fas fa-file-pdf'></i>
                </button>
              </div>
            </div>
            <div className='col-md-2'>
              <label className='form-label'>Vendedor</label>
              <Select
                classNamePrefix='react-select'
                placeholder='Selecione ou pesquise vendedor'
                isSearchable
                options={sellerOptions}
                value={sellerOptions.find((option) => option.value === sellerFilter) || sellerOptions[0]}
                onChange={(option) => setSellerFilter(option?.value || 'all')}
                onInputChange={(value, actionMeta) => {
                  if (actionMeta.action === 'input-change') {
                    setSellerSearch(value)
                  }
                }}
              />
            </div>
            <div className='col-md-2'>
              <label className='form-label'>Resultado</label>
              <Select
                classNamePrefix='react-select'
                placeholder='Selecione ou pesquise resultado'
                isSearchable
                options={outcomeOptions}
                value={outcomeOptions.find((option) => option.value === outcomeFilter) || outcomeOptions[0]}
                onChange={(option) => setOutcomeFilter(option?.value || 'all')}
                onInputChange={(value, actionMeta) => {
                  if (actionMeta.action === 'input-change') {
                    setOutcomeSearch(value)
                  }
                }}
              />
            </div>
            <div className='col-md-2'>
              <label className='form-label'>Motivo</label>
              <Select
                classNamePrefix='react-select'
                placeholder='Selecione ou pesquise motivo'
                isSearchable
                options={motiveOptions}
                value={motiveOptions.find((option) => option.value === selectedMotive) || motiveOptions[0]}
                onChange={(option) => setSelectedMotive(option?.value || 'all')}
                onInputChange={(value, actionMeta) => {
                  if (actionMeta.action === 'input-change') {
                    setMotiveSearch(value)
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className='card'>
        <div className='card-header'>
          <div className='d-flex flex-column w-100 gap-3'>
            <h3 className='card-title mb-0'>Histórico de Atendimentos</h3>
            <div className='d-flex align-items-center gap-2'>
              <span className='text-muted fs-7'>Itens por página</span>
              <select
                className='form-select form-select-sm'
                style={{ width: 90 }}
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                {pageSizeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className='card-body'>
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 gy-7'>
              <thead>
                <tr className='fw-bold fs-6 text-gray-800'>
                  <th>Vendedor</th>
                  <th>Posição</th>
                  <th>Início</th>
                  <th>Fim</th>
                  <th>Duração</th>
                  <th>Resultado</th>
                  <th>Motivo</th>
                </tr>
              </thead>
              <tbody>
                {pagedSessions.length === 0 ? (
                  <tr>
                    <td colSpan={7} className='text-center py-5'>
                      Nenhum atendimento encontrado
                    </td>
                  </tr>
                ) : (
                  pagedSessions.map((session) => (
                    <tr key={session.id}>
                      <td>
                        <span className='fw-bold'>{session.sellerName || 'Vendedor ' + session.sellerId}</span>
                      </td>
                      <td className='text-center'>{session.queuePositionAtStart}</td>
                      <td>
                        <small>
                          {new Date(session.startedAt).toLocaleString('pt-BR')}
                        </small>
                      </td>
                      <td>
                        <small>
                          {session.finishedAt ? new Date(session.finishedAt).toLocaleTimeString('pt-BR') : '-'}
                        </small>
                      </td>
                      <td>{formatDuration(session.durationSeconds)}</td>
                      <td>
                        <span className={`badge badge-light-${getOutcomeColor(session.outcome)}`}>
                          {getOutcomeLabel(session.outcome)}
                        </span>
                      </td>
                      <td>{getMotiveLabel(session)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {filteredSessions.length > 0 && (
            <div className='d-flex justify-content-between align-items-center mt-4'>
              <div></div>

              <div className='text-muted fs-7'>
                Exibindo {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, filteredSessions.length)} de {filteredSessions.length}
              </div>

              <div className='d-flex align-items-center gap-2'>
                <button
                  className='btn btn-light btn-sm'
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Anterior
                </button>
                <span className='fw-semibold fs-7'>
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  className='btn btn-light btn-sm'
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
